/**
 * Chat/analysis tasks still running on the server, persisted per user so a
 * reload (or another visit) can re-attach to them and show the thinking
 * bubble until they finish.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';

import type { PendingTask } from './chatConstants';
import { mergeStoredPendingTasks } from './pendingTasks';

export function usePendingTasks(pendingTasksStorageKey: string | null) {
  const [pendingTasks, setPendingTasks] = useState<PendingTask[]>([]);
  const [pendingTasksHydrated, setPendingTasksHydrated] = useState(false);
  const pendingTasksRef = useRef<PendingTask[]>([]);

  useEffect(() => {
    pendingTasksRef.current = pendingTasks;
  }, [pendingTasks]);

  useEffect(() => {
    setPendingTasks([]);
    setPendingTasksHydrated(false);
    if (!pendingTasksStorageKey) return;

    let cancelled = false;
    const loadPending = async () => {
      try {
        const raw = await AsyncStorage.getItem(pendingTasksStorageKey);
        if (!raw) return;
        const parsed = JSON.parse(raw) as PendingTask[];
        const merged = mergeStoredPendingTasks(pendingTasksRef.current, parsed, Date.now());
        if (!cancelled) setPendingTasks(merged);
      } catch (err) {
        console.warn('Failed to load pending tasks:', err);
      } finally {
        if (!cancelled) setPendingTasksHydrated(true);
      }
    };
    loadPending();
    return () => {
      cancelled = true;
    };
  }, [pendingTasksStorageKey]);

  useEffect(() => {
    if (!pendingTasksHydrated || !pendingTasksStorageKey) return;
    const persist = async () => {
      try {
        await AsyncStorage.setItem(pendingTasksStorageKey, JSON.stringify(pendingTasks));
      } catch (err) {
        console.warn('Failed to persist pending tasks:', err);
      }
    };
    persist();
  }, [pendingTasks, pendingTasksHydrated, pendingTasksStorageKey]);

  const addPendingTask = useCallback((task: PendingTask) => {
    setPendingTasks((prev) => {
      const filtered = prev.filter((t) => t.taskId !== task.taskId);
      return [...filtered, task];
    });
  }, []);

  const removePendingTask = useCallback((taskId: string) => {
    setPendingTasks((prev) => prev.filter((t) => t.taskId !== taskId));
  }, []);

  const getPendingTaskForSession = useCallback((sessionId: number) => {
    return pendingTasksRef.current.find((task) => task.sessionId === sessionId);
  }, []);

  return { pendingTasks, addPendingTask, removePendingTask, getPendingTaskForSession };
}
